/**
 * 补录手机号（更换房主、拆分联房）
 */
import React, { useImperativeHandle, useMemo, useRef, useState } from 'react';
import ModalForm, { ImodalFormRef } from '@/components/ModalForm';
import { IFieldItem } from '@/components/CustomForm';
import { ContractType } from '@/config';
import PhoneCheckModal, { PhoneCheckModalRef } from '@/components/PhoneCheckModal';
import { renderIntlLabel } from '@/utils';
import { TenantGlobalConfig } from '@/components/Intl/IntlContext/helper';
import BaseEmailInput, { EMAIL_REG } from '@/components/BaseEmailInput';
import BaseMobileInput from '@/components/BaseMobileInput';
import { parseAreaMobileStr } from '@/components/BaseMobileInput/helper';

type params = { record: any; callback: (values: any, record: any) => void };
export interface IadditionalRecordMobileRef extends Omit<ImodalFormRef, 'openModal'> {
  openModal: (value: params) => void;
}

interface AdditionalRecordMobileProps {
  myRef?: any;
  submitLoading?: boolean;
}
const AdditionalRecordMobile: React.FC<AdditionalRecordMobileProps> = ({
  myRef,
  submitLoading,
}) => {
  const [checkLoading, setCheckLoading] = useState<boolean>(false);
  const [detail, setDetail] = useState<any>({});

  const modalRef = useRef<ImodalFormRef>();
  const phoneCheckModalRef = useRef<PhoneCheckModalRef>();
  const paramsFnRef = useRef<any>();

  const defaultType = TenantGlobalConfig.getSystemConfig().contractType;

  const formAttr: Array<IFieldItem> = useMemo(() => {
    if (defaultType === ContractType.MOBILE) {
      return [
        {
          label: renderIntlLabel('order_roomorder_mobile'),
          key: 'mobile',
          type: 'group',
          component: <BaseMobileInput />,
          rules: [
            {
              validator(_, val) {
                const { areaCode, mobileStr } = parseAreaMobileStr(val);
                // 只填一个
                if ((areaCode && !mobileStr) || (!areaCode && mobileStr)) {
                  return Promise.reject(
                    new Error(
                      renderIntlLabel('order_frontDesk_validate_please_check_mobile_error_format'),
                    ),
                  );
                }
                if (!val || val.includes('*')) {
                  return Promise.reject(
                    new Error(
                      renderIntlLabel('order_frontDesk_validate_please_check_mobile_error_format'),
                    ),
                  );
                }
                return Promise.resolve();
              },
            },
          ],
        },
      ];
    }
    if (defaultType === ContractType.EMAIL) {
      return [
        {
          label: renderIntlLabel('help_store_address_type_email'),
          key: 'email',
          type: 'group',
          component: <BaseEmailInput />,
          rules: [
            {
              validator(_, val) {
                if (!val || !EMAIL_REG.test(val)) {
                  return Promise.reject(
                    new Error(
                      renderIntlLabel('order_frontDesk_validate_please_check_email_error_format'),
                    ),
                  );
                }
                return Promise.resolve();
              },
            },
          ],
        },
      ];
    }
    return [];
  }, []);

  const handleOpenModal = (values) => {
    const { record, callback } = values || {};
    setDetail(record);
    paramsFnRef.current = callback;
    modalRef.current.openModal();
  };

  const onOk = async (values) => {
    setCheckLoading(true);
    const { wyuId: wyuid, name } = detail;
    const { mobile, email } = values;
    try {
      if (wyuid && (mobile || email)) {
        phoneCheckModalRef.current
          .showPhoneCheck([{ wyuid, name, mobile, email }])
          .then((res) => {
            if (res.success && typeof paramsFnRef.current === 'function') {
              paramsFnRef.current(values, detail);
            }
          })
          .finally(() => {
            setCheckLoading(false);
          });
      } else {
        // 可能获取网鱼uid接口挂掉或者根本没有网鱼uid
        if (typeof paramsFnRef.current === 'function') {
          paramsFnRef.current(values, detail);
        }
        setCheckLoading(false);
      }
    } catch (error) {
      setCheckLoading(false);
    }
  };

  useImperativeHandle(
    myRef,
    () => ({
      ...modalRef.current,
      openModal: handleOpenModal,
    }),
    [],
  );

  return (
    <>
      <ModalForm
        width={700}
        ref={modalRef}
        title={renderIntlLabel('order_action_contact_information_supplement')}
        formProps={{ labelCol: { span: 4, offset: 0 }, wrapperCol: { span: 14 } }}
        formAttr={formAttr}
        onOk={onOk}
        confirmLoading={checkLoading || submitLoading}
      >
        <PhoneCheckModal innerRef={phoneCheckModalRef} />

        <div style={{ marginBottom: '16px' }}>
          {renderIntlLabel('order_msg_additional_recording_mobile', { name: detail?.name })}
        </div>
      </ModalForm>
    </>
  );
};

export default React.forwardRef<IadditionalRecordMobileRef, AdditionalRecordMobileProps>(
  (props, ref) => <AdditionalRecordMobile {...props} myRef={ref} />,
);
